/* ============================================================================
 * useEasReads — EAS indexer reads, cached through the app's query client
 * ----------------------------------------------------------------------------
 * These wrap lib/eas-index.ts. The query function never throws on a failed
 * read: it RESOLVES to the discriminated `EasRead` union, so a component is
 * forced to branch on `status` before touching `data`. A rejected promise would
 * instead surface as an exception the UI can quietly swallow, which is how an
 * unreachable indexer turns into a rendered "0".
 *
 * `enabled` gates every read on having a subject: without it, a hook would fire
 * with an undefined address and the failure would look like a protocol error
 * rather than "not connected".
 * ========================================================================= */

import { useQuery } from '@tanstack/react-query';
import {
  fetchAttestationCount,
  fetchAttestationsReceived,
  fetchSchemaRegistration,
  type EasRead,
  type IndexedAttestation,
  type RegisteredSchema,
} from '../lib/eas-index';

/** Reads are cheap but a public indexer is not free; a minute is plenty. */
const STALE_MS = 60_000;

export function useAttestationsReceived(
  chainId: number,
  recipient: string | undefined,
  opts: { schemaId?: string; take?: number } = {},
) {
  const take = opts.take ?? 12;
  const query = useQuery({
    queryKey: ['eas', 'received', chainId, recipient ?? null, opts.schemaId ?? null, take],
    enabled: Boolean(recipient),
    staleTime: STALE_MS,
    queryFn: (): Promise<EasRead<IndexedAttestation[]>> =>
      fetchAttestationsReceived(chainId, recipient as string, {
        schemaId: opts.schemaId,
        take,
      }),
  });

  const read: EasRead<IndexedAttestation[]> | undefined = recipient
    ? query.data
    : { status: 'no-subject', reason: 'connect a wallet to read its attestations' };

  return { read, isPending: query.isPending, refetch: query.refetch };
}

export function useAttestationCount(
  chainId: number,
  recipient: string | undefined,
  opts: { schemaId?: string } = {},
) {
  const query = useQuery({
    queryKey: ['eas', 'count', chainId, recipient ?? null, opts.schemaId ?? null],
    enabled: Boolean(recipient),
    staleTime: STALE_MS,
    queryFn: (): Promise<EasRead<number>> =>
      fetchAttestationCount(chainId, recipient as string, {
        schemaId: opts.schemaId,
      }),
  });

  const read: EasRead<number> | undefined = recipient
    ? query.data
    : { status: 'no-subject', reason: 'connect a wallet to count its attestations' };

  return { read, isPending: query.isPending, refetch: query.refetch };
}

/**
 * Is a schema UID registered on this chain? Pass `undefined` to skip (e.g. a
 * PROPOSED schema whose UID only becomes meaningful at deploy time) — the hook
 * then reports `no-subject` rather than asking about a UID nobody registered.
 */
export function useSchemaRegistration(chainId: number, uid: string | undefined) {
  const query = useQuery({
    queryKey: ['eas', 'schema', chainId, uid ?? null],
    enabled: Boolean(uid),
    staleTime: 5 * 60_000,
    queryFn: (): Promise<EasRead<RegisteredSchema | null>> =>
      fetchSchemaRegistration(chainId, uid as string),
  });

  const read: EasRead<RegisteredSchema | null> | undefined = uid
    ? query.data
    : { status: 'no-subject', reason: 'no schema UID to look up' };

  return { read, isPending: query.isPending, refetch: query.refetch };
}
